/**
 * URL + cURL synthesis + executor for the API Playground.
 *
 * All endpoints flow through `executeEndpoint`, which fetches the displayed
 * URL with the partner's Firebase ID token. What `buildCurl` shows is what
 * the playground actually executes — no separate code path.
 */
import type { Endpoint, ParamValues, RunResult } from ".";

/**
 * Base URL of the v1 partner REST API. Same Hono server that serves /v1/qa
 * (the QA reviewer endpoints), so we reuse `VITE_QA_API_URL`.
 *   - dev: `http://localhost:3100/v1`
 *   - prod: `<deployed app engine host>/v1`
 */
export const API_BASE_URL = `${
  import.meta.env.VITE_QA_API_URL || "http://localhost:3100"
}/v1`;

const TOKEN_PLACEHOLDER = "$YOUR_FIREBASE_ID_TOKEN";

export function buildRequestUrl(endpoint: Endpoint, values: ParamValues): string {
  const pathWithValues = endpoint.path.replace(/\{(\w+)\}/g, (_, name) =>
    encodeURIComponent(values[name] ?? ""),
  );
  const queryParts: string[] = [];
  for (const p of endpoint.params) {
    if (p.in !== "query") continue;
    const v = values[p.name];
    if (v && v.trim() !== "") {
      queryParts.push(`${encodeURIComponent(p.name)}=${encodeURIComponent(v)}`);
    }
  }
  const qs = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
  return `${API_BASE_URL}${pathWithValues}${qs}`;
}

export function buildCurl(
  endpoint: Endpoint,
  values: ParamValues,
  token: string | null,
): string {
  const url = buildRequestUrl(endpoint, values);
  const tokenValue = token ?? TOKEN_PLACEHOLDER;
  return [
    `curl -X ${endpoint.method} "${url}" \\`,
    `  -H "Authorization: Bearer ${tokenValue}"`,
  ].join("\n");
}

/**
 * Execute the displayed request. Wraps `fetch` in a `RunResult` envelope so
 * the playground UI can render status / body / error uniformly.
 *
 * - `data`: parsed JSON body on 2xx, otherwise null.
 * - `error`: parsed JSON body on non-2xx, otherwise null.
 * - `status`: real HTTP status from the server.
 */
export async function executeEndpoint(
  endpoint: Endpoint,
  values: ParamValues,
  token: string,
): Promise<RunResult> {
  const url = buildRequestUrl(endpoint, values);
  try {
    const res = await fetch(url, {
      method: endpoint.method,
      headers: { Authorization: `Bearer ${token}` },
    });
    const body = await res.json().catch(() => null);
    return {
      data: res.ok ? body : null,
      error: res.ok ? null : body,
      status: res.status,
      statusText: res.statusText,
      count: null,
    };
  } catch (e) {
    // Network-level failure (DNS, CORS, server down, connection drop, etc).
    // Return a synthetic RunResult so the playground renders it through the
    // standard response panel instead of falling out to an error toast.
    // `status: 0` mirrors the browser's own convention for "no HTTP
    // response received" and disambiguates from any real server status.
    const message = e instanceof Error ? e.message : String(e);
    return {
      data: null,
      error: { error: "NetworkError", message },
      status: 0,
      statusText: "Network Error",
      count: null,
    };
  }
}
